import React from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import LocationCityRoundedIcon from "@mui/icons-material/LocationCityRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ContactMailRoundedIcon from "@mui/icons-material/ContactMailRounded";
import ImageElementEditor from "./ImageElementEditor";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";

const FIELD_ICONS = Object.freeze({
  "business.phone": PhoneRoundedIcon,
  "business.email": EmailRoundedIcon,
  "business.address": PlaceRoundedIcon,
  "business.city": LocationCityRoundedIcon,
  "business.province": MapRoundedIcon,
  "business.country": PublicRoundedIcon,
  "business.schedule": ScheduleRoundedIcon,
  "business.socials.instagram": InstagramIcon,
  "business.socials.facebook": FacebookIcon,
  "business.socials.linkedin": LinkedInIcon,
  "business.socials.website": LanguageRoundedIcon,
  "business.mapEmbedUrl": LinkRoundedIcon,
  "contact.mapTitle": MapRoundedIcon,
  "footer.contactTitle": ContactMailRoundedIcon,
});

function compactField(field, form, disabled, onCommit) {
  if (!field) return null;
  const Icon = FIELD_ICONS[field.path] || LinkRoundedIcon;
  const value = getValueAtDescriptorPath(form, field.path, "") ?? "";
  const multiline = field.type === SEGMENT_FIELD_TYPES.TEXTAREA;
  const wide = multiline || ["business.address", "business.mapEmbedUrl", "contact.mapTitle", "footer.contactTitle"].includes(field.path);

  return (
    <TextField
      key={field.path}
      fullWidth
      size="small"
      type={field.type === SEGMENT_FIELD_TYPES.EMAIL ? "email" : "text"}
      label={field.label}
      value={value}
      disabled={disabled}
      multiline={multiline}
      minRows={multiline ? 2 : undefined}
      inputProps={{ maxLength: field.maxLength }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Icon fontSize="small" />
          </InputAdornment>
        ),
      }}
      onChange={(event) => onCommit?.(field.path, event.target.value)}
      sx={{ gridColumn: wide ? "1 / -1" : "auto" }}
    />
  );
}

function sharedChip(fields = []) {
  return fields.some((field) => field?.shared) ? (
    <Chip
      size="small"
      label="Se usa también en otras secciones"
      variant="outlined"
      sx={{ alignSelf: "flex-start" }}
    />
  ) : null;
}

export default function ContactElementEditor({
  descriptor,
  form,
  disabled,
  onCommit,
  onUploadImage,
  onRemoveImage,
}) {
  const mode = descriptor?.editorMode || "business";
  const fields = descriptor?.fields || [];

  if (mode === "map") {
    const mapUrlField = fields.find((field) => field.path === "business.mapEmbedUrl") || null;
    const mapTitleField = fields.find((field) => field.path === "contact.mapTitle") || null;
    const imageField = descriptor?.imageField || null;
    const mapConnected = Boolean(getValueAtDescriptorPath(form, mapUrlField?.path, ""));

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
          <Chip size="small" label="Imagen" variant="outlined" />
          <Chip size="small" label={mapConnected ? "Mapa conectado" : "Mapa sin configurar"} variant={mapConnected ? "filled" : "outlined"} />
          {mapUrlField?.shared ? <Chip size="small" label="Dato compartido" variant="outlined" /> : null}
        </Stack>

        {imageField ? (
          <Box sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 950 }}>Imagen del bloque</Typography>
            <ImageElementEditor
              descriptor={{
                ...descriptor,
                kind: "image",
                field: imageField,
                fieldPath: imageField.path,
                altField: null,
              }}
              form={form}
              disabled={disabled}
              onCommit={onCommit}
              onUploadImage={onUploadImage}
              onRemoveImage={onRemoveImage}
            />
          </Box>
        ) : null}

        <Box sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5 }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>Mapa</Typography>
              {mapUrlField?.shared ? <Chip size="small" label="Se usa también en otras secciones" variant="outlined" /> : null}
            </Stack>
            {compactField(mapTitleField, form, disabled, onCommit)}
            {compactField(mapUrlField, form, disabled, onCommit)}
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
              Usá la misma URL de mapa que ya utiliza el negocio; no se crea una copia para esta sección.
            </Typography>
          </Stack>
        </Box>
      </Stack>
    );
  }

  const title = mode === "socials" ? "Redes sociales" : mode === "footer-contact" ? "Contacto del footer" : "Datos del negocio";
  return (
    <Stack spacing={1.3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>{title}</Typography>
        {sharedChip(fields)}
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1,
          "@media (max-width:430px)": { gridTemplateColumns: "1fr" },
        }}
      >
        {fields.map((field) => compactField(field, form, disabled, onCommit))}
      </Box>
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
        {mode === "socials"
          ? "Estos enlaces son datos compartidos del negocio."
          : mode === "footer-contact"
            ? "El título pertenece al footer; los datos del negocio se reutilizan sin copias."
            : "Los cambios se reflejan donde estos datos del negocio también se utilizan."}
      </Typography>
    </Stack>
  );
}
